import React, { useMemo, useState } from "react"
import { useStateMachine } from "components/context/stateMachine/StateMachineContext"
import { Steps } from "components/csd-enrollment/workflow/steps"
import L10n from "lib/carrot-l10n"
import { Body, Container, H2, DateInput } from "@carrotfertility/carotene"
import { BenefitEnrollmentFlowModalBackButton, BenefitEnrollmentModalContinueButton } from "../../shared"
import moment from "moment"
import useSubmitWithErrorCatch from "../../hooks/useSubmitWithErrorCatch"
import { useCurrentUser } from "../../../context/user/UserContext"
import { CSD_CONFIGURATION, useUserDeductible } from "components/context/user/DeductibleContext"
import { useBenefitEnrollment } from "components/context/enrollment/BenefitEnrollmentContext"
import { FormProvider, useForm } from "react-hook-form"
import PlanPeriodWarning from "components/csd-enrollment/shared/PlanPeriodWarning"
import { dateRangeContainsLeapDay, DAYS_IN_YEAR, DAYS_IN_LEAP_YEAR } from "components/csd-enrollment/util/date-utils"
import styled from "styled-components"

const l = new L10n({ locale: "en-US" }).getBoundLocalize()

const BoldSpan = styled.span`
  font-weight: 700;
`
interface CoverageDateStepProps {
  title: string
  description: string
  stepName: string
  isMember?: boolean
  initialValue: string | null
  periodLengthWarningDescription: string
  coverageRangeReminder?: string
}

function CoverageDateStep({
  title,
  description,
  stepName,
  isMember,
  initialValue,
  periodLengthWarningDescription,
  coverageRangeReminder
}: CoverageDateStepProps): JSX.Element {
  const formMethods = useForm()

  const coverageStartDateKey = isMember ? "memberCoverageStartDate" : "partnerCoverageStartDate"
  const coverageEndDateKey = isMember ? "memberCoverageEndDate" : "partnerCoverageEndDate"

  const { setNextStep, goBackTwoSteps } = useStateMachine(stepName, initialValue)
  const { [coverageStartDateKey]: startDate, [coverageEndDateKey]: endDate, updateUserInfo } = useCurrentUser()

  const defaultStartDate = useMemo(() => moment(startDate).format("YYYY-MM-DD"), [startDate])
  const defaultEndDate = useMemo(() => moment(endDate).format("YYYY-MM-DD"), [endDate])
  const currentYear = new Date().getFullYear()

  const [currentStartDate, setCurrentStartDate] = useState(defaultStartDate)
  const [currentEndDate, setCurrentEndDate] = useState(defaultEndDate)

  const { onSubmit } = useSubmitWithErrorCatch(async (args) => {
    const { coverageDateStart, coverageDateEnd } = args
    const startDateVal = moment(coverageDateStart)
    const endDateVal = moment(coverageDateEnd)

    const today = moment().format("YYYY-MM-DD")

    const invalidStartDate = !startDateVal.isSameOrBefore(today, "day")
    const invalidEndDate = !endDateVal.isSameOrAfter(today, "day")

    invalidStartDate &&
      formMethods.setError("coverageDateStart", { type: "manual", message: "Start date must be in the past" })
    invalidEndDate &&
      formMethods.setError("coverageDateEnd", { type: "manual", message: "End date must be in the future" })

    if (!(invalidStartDate || invalidEndDate)) {
      await updateUserInfo({
        [coverageStartDateKey]: startDateVal.format("YYYY-MM-DD"),
        [coverageEndDateKey]: endDateVal.format("YYYY-MM-DD")
      })
      setNextStep(stepName, "")
    }
  })

  const onEndDateChange = (date): void => {
    setCurrentEndDate(date)
    formMethods.clearErrors("coverageDateEnd")
  }

  const onStartDateChange = (date): void => {
    setCurrentStartDate(date)
    if (date) {
      const startDate = new Date(date)
      if (startDate.getUTCMonth() == 0 && startDate.getUTCDate() == 1) {
        // month is zero-indexed but date is 1
        const newEndDate = `${startDate.getUTCFullYear()}-12-31`
        setCurrentEndDate(moment(newEndDate).format("YYYY-MM-DD"))
        onEndDateChange(newEndDate)
      }
    }
    formMethods.clearErrors("coverageDateStart")
  }

  const daysInRange = moment(currentEndDate).add(1, "day").diff(currentStartDate, "day")
  const warnUserForUnusualPlanPeriod =
    daysInRange > (dateRangeContainsLeapDay(currentStartDate, currentEndDate) ? DAYS_IN_LEAP_YEAR : DAYS_IN_YEAR)

  return (
    <FormProvider {...formMethods}>
      <Container stack="huge">
        <BenefitEnrollmentFlowModalBackButton handleGoBack={goBackTwoSteps} />
      </Container>
      <H2 stack="tiny" variant="primary">
        {title}
      </H2>
      <Container stack="huge">
        <Body color="black-80">{description}</Body>
      </Container>

      {coverageRangeReminder && (
        <Container stack="huge">
          <Body color="black-80">
            <BoldSpan className="bold">Note: </BoldSpan>
            {coverageRangeReminder}
          </Body>
        </Container>
      )}

      <Container stack="huge">
        <DateInput
          error={Boolean(formMethods.errors["coverageDateStart"])}
          errorMessage={formMethods.errors["coverageDateStart"]?.message}
          ref={formMethods.register({ required: "Required" })}
          onChange={onStartDateChange}
          defaultValue={defaultStartDate}
          name="coverageDateStart"
          label="Start Date"
          numberYearsSupported={3}
          startingYear={currentYear}
          required
        />
      </Container>

      <Container stack="huge">
        <DateInput
          error={Boolean(formMethods.errors["coverageDateEnd"])}
          errorMessage={formMethods.errors["coverageDateEnd"]?.message}
          ref={formMethods.register({ required: "Required" })}
          onChange={onEndDateChange}
          defaultValue={currentEndDate}
          name="coverageDateEnd"
          label="End Date"
          numberYearsSupported={3}
          startingYear={currentYear + 2}
          required
          key={currentEndDate}
        />
      </Container>
      {warnUserForUnusualPlanPeriod && (
        <PlanPeriodWarning
          warningDescription={periodLengthWarningDescription}
          warningTitle={`The period you selected is over a year (${daysInRange} days)`}
        />
      )}
      <BenefitEnrollmentModalContinueButton onClick={formMethods.handleSubmit(onSubmit)} />
    </FormProvider>
  )
}

export function MemberPlanCoverageDateStep(): JSX.Element {
  const {
    company: { parentCompanyName, companyName, medicalPlanStartDate, medicalPlanEndDate }
  } = useCurrentUser()

  const membersCompanyName = parentCompanyName ? parentCompanyName : companyName

  const { csdConfigurationPreview } = useUserDeductible()
  const { shouldSkipMemberCoverageDate } = useBenefitEnrollment()
  const collectDeductiblePeriod = [
    CSD_CONFIGURATION.MEMBER_AND_PARTNER_SEPARATE,
    CSD_CONFIGURATION.MEMBER_AND_PARTNER_COMBINED,
    CSD_CONFIGURATION.MEMBER_ONLY
  ].includes(csdConfigurationPreview)
  const textProps = collectDeductiblePeriod
    ? {
        title: l`When does the annual deductible period on your medical plan start and end?`,
        description: l`This often aligns with plan start and end dates, which may be listed on your insurance card. 
        The accuracy of your answer can have financial implications, 
        so if you're not sure, please check with the relevant insurance provider or HR department.`,
        periodLengthWarningDescription: "Please check your dates. Deductible periods are typically a year.",
        coverageRangeReminder:
          medicalPlanStartDate &&
          medicalPlanEndDate &&
          `If you are enrolled in ${membersCompanyName}'s medical plan, your deductible period is ${moment(
            medicalPlanStartDate
          ).format("MM/DD/YYYY")} to ${moment(medicalPlanEndDate).format("MM/DD/YYYY")}.`
      }
    : {
        title: l`When does your medical plan year start and end?`,
        description: l`These dates may be listed on your insurance card.
        The accuracy of your answer can have financial implications, so if you're not sure, 
        please check with the relevant insurance provider or HR department.`,
        periodLengthWarningDescription: "Please check your dates. Plan periods are typically a year.",
        coverageRangeReminder:
          medicalPlanStartDate &&
          medicalPlanEndDate &&
          `If you are enrolled in ${membersCompanyName}'s medical plan, your plan year is ${moment(
            medicalPlanStartDate
          ).format("MM/DD/YYYY")} to ${moment(medicalPlanEndDate).format("MM/DD/YYYY")}.`
      }
  return (
    <CoverageDateStep
      {...textProps}
      initialValue={shouldSkipMemberCoverageDate ? "" : null}
      stepName={Steps.MEMBER_DEDUCTIBLE_PERIOD_COVERAGE_DATE}
      isMember
    />
  )
}

export function PartnerPlanCoverageDateStep(): JSX.Element {
  const { csdConfigurationPreview } = useUserDeductible()
  const { shouldSkipPartnerCoverageDate } = useBenefitEnrollment()

  const collectDeductiblePeriod = [
    CSD_CONFIGURATION.MEMBER_AND_PARTNER_SEPARATE,
    CSD_CONFIGURATION.PARTNER_ONLY
  ].includes(csdConfigurationPreview)

  const textProps = collectDeductiblePeriod
    ? {
        title: l`When does the annual deductible period on your partner's medical plan start and end?`,
        description: l`This often aligns with coverage start and end dates, which may be listed on their insurance card. 
        The accuracy of your answer may have financial implications, so if you're not sure, please check with the 
        relevant insurance provider or HR department.`,
        periodLengthWarningDescription: "Please check your dates. Deductible periods are typically a year."
      }
    : {
        title: l`When does your partner's medical plan coverage start and end?`,
        description: l`These dates may be listed on their insurance card. The accuracy of your answer may have financial implications, so if you're not sure, please check with the relevant insurance provider or HR department.`,
        periodLengthWarningDescription: "Please check your dates. Plan periods are typically a year."
      }
  return (
    <CoverageDateStep
      {...textProps}
      initialValue={shouldSkipPartnerCoverageDate ? "" : null}
      stepName={Steps.PARTNER_DEDUCTIBLE_PERIOD_COVERAGE_DATE}
    />
  )
}
